import { useState } from 'react'
import { App as AntdApp, Checkbox, Form, Grid, Input, Modal, Typography } from 'antd'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import apiClient from '../api/client'
import SignaturePad from './SignaturePad'

// Version of the legal mandate text the customer signs — recorded by the backend.
export const MANDATE_TEXT_VERSION = 'sepa-mandate-de-v1'

// SepaMandateModal is the single SEPA-mandate creation form for the customer
// portal. It captures the drawn signature (required by the backend) and is used
// from every place that lets a customer add a mandate (Payment Methods, Profile,
// Billing) — there is intentionally only ONE implementation.
export default function SepaMandateModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const screens = Grid.useBreakpoint()
  const [form] = Form.useForm()
  const [signature, setSignature] = useState<string | null>(null)
  const debtorName = Form.useWatch('debtor_name', form)
  const iban = Form.useWatch('iban', form)
  // Auslands-IBAN (nicht DE) → BIC ist Pflicht, sonst weist die Bank die
  // Lastschrift ab (z.B. Schweizer CH-Konten brauchen zwingend den BIC).
  const bicRequired = !!iban && !iban.replace(/\s/g, '').toUpperCase().startsWith('DE')

  const creditorQ = useQuery({
    queryKey: ['sepa-creditor'],
    queryFn: async () =>
      (await apiClient.get<{ data: { creditor_name: string; creditor_id: string } }>('/api/v1/public/sepa-creditor')).data.data,
    enabled: open,
  })
  const creditorName = creditorQ.data?.creditor_name || '—'
  const creditorId = creditorQ.data?.creditor_id || '—'

  const create = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      await apiClient.post('/api/v1/portal/mandates', values)
    },
    onSuccess: () => {
      message.success(t('portal.mandates.created', 'Mandat erteilt'))
      form.resetFields()
      setSignature(null)
      onCreated()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error
        || t('portal.mandates.createFailed', 'Mandat konnte nicht angelegt werden'))
    },
  })

  const submit = (v: Record<string, unknown>) => {
    if (!signature) {
      message.error(t('portal.mandates.needSignature', 'Bitte unterschreiben Sie das Mandat.'))
      return
    }
    create.mutate({
      debtor_name: v.debtor_name, iban: v.iban, bic: v.bic,
      signature_image: signature, mandate_text_version: MANDATE_TEXT_VERSION,
    })
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('portal.mandates.addTitle', 'SEPA-Lastschriftmandat erteilen')}
      onOk={() => form.submit()}
      okText={t('portal.mandates.grant', 'Mandat erteilen')}
      okButtonProps={{ loading: create.isPending }}
      width={screens.md ? 600 : '95vw'}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="debtor_name" label={t('portal.mandates.debtorName', 'Kontoinhaber (Name)')} rules={[{ required: true }]}>
          <Input placeholder="Max Mustermann" />
        </Form.Item>
        <Form.Item name="iban" label={t('portal.mandates.iban', 'IBAN')} rules={[{ required: true }]}>
          <Input placeholder="DE12 3456 7890 1234 5678 90" />
        </Form.Item>
        <Form.Item
          name="bic"
          label={bicRequired
            ? t('portal.mandates.bicRequired', 'BIC (für Auslandskonto erforderlich)')
            : t('portal.mandates.bic', 'BIC (optional)')}
          rules={bicRequired ? [{ required: true, message: t('portal.mandates.bicNeeded', 'Für eine Nicht-DE-IBAN ist der BIC erforderlich.') }] : []}
        >
          <Input placeholder="COBADEFFXXX" />
        </Form.Item>

        <div className="sepa-mandate-text">
          <Typography.Paragraph className="text-sm mb-4">
            {t('portal.mandates.text1', 'Ich ermächtige')} <strong>{creditorName}</strong> ({t('portal.mandates.creditorId', 'Gläubiger-ID')}: <Typography.Text code>{creditorId}</Typography.Text>),
            {' '}{t('portal.mandates.text2', 'Zahlungen von meinem Konto')} {iban ? <Typography.Text code>{iban}</Typography.Text> : null} {t('portal.mandates.text3', 'mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von')} <strong>{creditorName}</strong> {t('portal.mandates.text4', 'auf mein Konto gezogenen Lastschriften einzulösen.')}
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary" className="text-xs mb-0">
            {t('portal.mandates.text5', 'Hinweis: Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten die mit meinem Kreditinstitut vereinbarten Bedingungen. Kontoinhaber:')} <strong>{debtorName || '—'}</strong>. {t('portal.mandates.refNote', 'Die Mandatsreferenz wird bei Erteilung vergeben.')}
          </Typography.Paragraph>
        </div>

        <Form.Item label={t('portal.mandates.signature', 'Unterschrift')} required className="mt-12">
          <SignaturePad onChange={setSignature} />
        </Form.Item>

        <Form.Item
          name="agree"
          valuePropName="checked"
          rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject(new Error(t('portal.mandates.mustAgree', 'Bitte bestätigen Sie die Erteilung.'))) }]}
        >
          <Checkbox>{t('portal.mandates.agree', 'Ich erteile das oben stehende SEPA-Lastschriftmandat und bestätige meine Unterschrift.')}</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  )
}
